/**
 * OrderedMultiSelectPickList.jsx
 *
 * A production-ready, reusable ordered multi-select pick list component.
 * Built with plain JavaScript React (functional components + hooks) and
 * @fluentui/react-components.
 *
 * Layout:
 *   [Available Items] [↔ Add/Remove] [Selected Items] [↕ Up/Down] [Final Order Preview]
 *
 * Features:
 *   - Search / filter available items
 *   - Multi-select with accessible checkbox indicators
 *   - Add and Remove items between panels
 *   - Drag-and-drop reordering in the selected panel
 *   - Move Up / Move Down keyboard-accessible reorder fallback
 *   - Read-only final order preview with sequence numbers
 *   - Empty state, loading state, disabled state
 *   - Accessible: ARIA roles, labels, focus management, keyboard navigation
 *   - Optimised: useMemo for derived lists, useCallback for handlers
 */

import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
    Button,
    Input,
    Text,
    Badge,
    Spinner,
    Tooltip,
    makeStyles,
    mergeClasses,
    tokens,
} from "@fluentui/react-components";
import {
    ArrowRight16Regular,
    ArrowLeft16Regular,
    ArrowUp16Regular,
    ArrowDown16Regular,
    Search16Regular,
    ReOrderDotsVertical16Regular,
} from "@fluentui/react-icons";

// ─────────────────────────────────────────────────────────────────────────────
// Styles (Griffel / makeStyles)
// ─────────────────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
    // ── Outer wrapper ─────────────────────────────────────────────────────
    root: {
        display: "flex",
        flexDirection: "row",
        gap: "6px",
        alignItems: "stretch",
        width: "100%",
        boxSizing: "border-box",
    },

    // ── Panel shell (shared by all three content panels) ──────────────────
    panel: {
        display: "flex",
        flexDirection: "column",
        flex: "1 1 0",
        minWidth: 0,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorNeutralBackground1,
        overflow: "hidden",
    },

    panelHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 12px",
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: tokens.colorNeutralBackground2,
        flexShrink: 0,
    },

    panelTitle: {
        fontWeight: 600,
        fontSize: "13px",
        color: tokens.colorNeutralForeground1,
    },

    // ── Search bar ────────────────────────────────────────────────────────
    searchBar: {
        padding: "6px 8px",
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        flexShrink: 0,
    },

    // ── Scrollable list body ───────────────────────────────────────────────
    listBody: {
        flex: 1,
        overflowY: "auto",
    },

    // ── List row ──────────────────────────────────────────────────────────
    listItem: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "5px 10px",
        borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
        cursor: "pointer",
        userSelect: "none",
        outline: "none",
        ":hover": {
            backgroundColor: tokens.colorSubtleBackgroundHover,
        },
        ":focus-visible": {
            boxShadow: `inset 0 0 0 2px ${tokens.colorBrandBackground}`,
        },
    },

    listItemChecked: {
        backgroundColor: tokens.colorSubtleBackgroundSelected,
        ":hover": {
            backgroundColor: tokens.colorSubtleBackgroundSelected,
        },
    },

    listItemDragging: {
        opacity: "0.35",
        backgroundColor: tokens.colorNeutralBackground3,
    },

    listItemDragTarget: {
        borderTop: `2px solid ${tokens.colorBrandBackground}`,
    },

    // ── Custom checkbox indicator (row is the interactive element) ─────────
    checkboxBox: {
        flexShrink: 0,
        width: "14px",
        height: "14px",
        border: `1.5px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: "3px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: tokens.colorNeutralBackground1,
        pointerEvents: "none", // interaction handled by the row
    },

    checkboxBoxChecked: {
        backgroundColor: tokens.colorBrandBackground,
        borderTopColor: tokens.colorBrandBackground,
        borderRightColor: tokens.colorBrandBackground,
        borderBottomColor: tokens.colorBrandBackground,
        borderLeftColor: tokens.colorBrandBackground,
    },

    // ── Drag handle ───────────────────────────────────────────────────────
    dragHandle: {
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        color: tokens.colorNeutralForeground3,
        cursor: "grab",
        ":active": {
            cursor: "grabbing",
        },
    },

    // ── Item label ────────────────────────────────────────────────────────
    itemLabel: {
        flex: 1,
        fontSize: "13px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        color: tokens.colorNeutralForeground1,
    },

    // ── Position number shown inside Selected panel ────────────────────────
    orderNum: {
        flexShrink: 0,
        fontSize: "11px",
        color: tokens.colorNeutralForeground3,
        minWidth: "22px",
        textAlign: "right",
    },

    // ── Narrow button columns (Add/Remove and Move Up/Down) ────────────────
    btnCol: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        flexShrink: 0,
        width: "36px",
    },

    // ── Empty state ───────────────────────────────────────────────────────
    emptyState: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "4px",
        padding: "32px 16px",
        textAlign: "center",
    },

    // ── Loading state ──────────────────────────────────────────────────────
    loadingState: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "32px 16px",
        flex: 1,
    },

    // ── Final Order Preview row ────────────────────────────────────────────
    previewItem: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "5px 10px",
        borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    },

    seqCircle: {
        flexShrink: 0,
        width: "22px",
        height: "22px",
        borderRadius: "50%",
        backgroundColor: tokens.colorBrandBackground,
        color: tokens.colorNeutralForegroundOnBrand,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "11px",
        fontWeight: 600,
    },
});

// ─────────────────────────────────────────────────────────────────────────────
// Inline SVG checkmark (avoids an external icon dependency for the checkbox)
// ─────────────────────────────────────────────────────────────────────────────

function CheckmarkSvg() {
    return (
        <svg
            width="9"
            height="9"
            viewBox="0 0 10 10"
            aria-hidden="true"
            focusable="false"
        >
            <polyline
                points="1.5,5.5 4,8 8.5,2"
                fill="none"
                stroke="white"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// OrderedMultiSelectPickList
// ─────────────────────────────────────────────────────────────────────────────

export default function OrderedMultiSelectPickList({
    availableItems,
    selectedItems = [],
    getItemId,
    getItemLabel,
    onSelectionChange,
    searchable = true,
    disabled = false,
    loading = false,
    height = 400,
    showFinalOrder = true,
}) {
    const s = useStyles();

    // ── Local UI state ───────────────────────────────────────────────────────
    /** Set of IDs that the user has checked in the Available panel */
    const [availChecked, setAvailChecked] = useState(new Set());
    /** Set of IDs that the user has checked in the Selected panel */
    const [selChecked, setSelChecked] = useState(new Set());
    /** Search query (filters the Available panel only) */
    const [search, setSearch] = useState("");
    /** Index of the row currently being dragged */
    const [dragSrc, setDragSrc] = useState(null);
    /** Index of the row the drag is hovering over */
    const [dragOver, setDragOver] = useState(null);

    // ── Derived values ───────────────────────────────────────────────────────

    /** O(1) lookup set — avoids O(n) includes() in filter callbacks */
    const selectedIdSet = useMemo(
        () => new Set(selectedItems.map(getItemId)),
        [selectedItems, getItemId],
    );

    /** Items that have not yet been added to the selected list */
    const availableList = useMemo(
        () => availableItems.filter((item) => !selectedIdSet.has(getItemId(item))),
        [availableItems, selectedIdSet, getItemId],
    );

    /** Available items after applying the search filter */
    const filteredAvailable = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return availableList;
        return availableList.filter((item) =>
            getItemLabel(item).toLowerCase().includes(q),
        );
    }, [availableList, search, getItemLabel]);

    /**
     * Move Up is enabled when at least one checked item is not already
     * at the very top of the selected list.
     */
    const canMoveUp = useMemo(() => {
        if (!selChecked.size) return false;
        return (
            selectedItems.findIndex((item) => selChecked.has(getItemId(item))) > 0
        );
    }, [selChecked, selectedItems, getItemId]);

    /**
     * Move Down is enabled when at least one checked item is not already
     * at the very bottom of the selected list.
     */
    const canMoveDown = useMemo(() => {
        if (!selChecked.size) return false;
        for (let i = selectedItems.length - 1; i >= 0; i--) {
            if (selChecked.has(getItemId(selectedItems[i]))) {
                return i < selectedItems.length - 1;
            }
        }
        return false;
    }, [selChecked, selectedItems, getItemId]);

    // ── Toggle helpers ───────────────────────────────────────────────────────

    const toggleAvailChecked = useCallback((id) => {
        if (disabled) return;
        setAvailChecked((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, [disabled]);

    const toggleSelChecked = useCallback((id) => {
        if (disabled) return;
        setSelChecked((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, [disabled]);

    // ── Transfer handlers ────────────────────────────────────────────────────

    /** Move all checked available items into the selected list */
    const handleAdd = useCallback(() => {
        if (disabled || !availChecked.size) return;
        // Preserve order from availableItems; guard against duplicates
        const toAdd = availableItems.filter(
            (item) =>
                availChecked.has(getItemId(item)) &&
                !selectedIdSet.has(getItemId(item)),
        );
        onSelectionChange([...selectedItems, ...toAdd]);
        setAvailChecked(new Set());
    }, [disabled, availChecked, availableItems, getItemId, selectedIdSet, selectedItems, onSelectionChange]);

    /** Move all checked selected items back into the available list */
    const handleRemove = useCallback(() => {
        if (disabled || !selChecked.size) return;
        onSelectionChange(
            selectedItems.filter((item) => !selChecked.has(getItemId(item))),
        );
        setSelChecked(new Set());
    }, [disabled, selChecked, selectedItems, getItemId, onSelectionChange]);

    // ── Reorder handlers ─────────────────────────────────────────────────────

    /**
     * Shift every checked item one position toward index 0.
     * Iterates forward so a block of consecutive checked items moves as a unit.
     */
    const handleMoveUp = useCallback(() => {
        if (disabled || !canMoveUp) return;
        const arr = [...selectedItems];
        for (let i = 1; i < arr.length; i++) {
            if (
                selChecked.has(getItemId(arr[i])) &&
                !selChecked.has(getItemId(arr[i - 1]))
            ) {
                [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
            }
        }
        onSelectionChange(arr);
    }, [disabled, canMoveUp, selectedItems, selChecked, getItemId, onSelectionChange]);

    /**
     * Shift every checked item one position toward the last index.
     * Iterates backward so a block of consecutive checked items moves as a unit.
     */
    const handleMoveDown = useCallback(() => {
        if (disabled || !canMoveDown) return;
        const arr = [...selectedItems];
        for (let i = arr.length - 2; i >= 0; i--) {
            if (
                selChecked.has(getItemId(arr[i])) &&
                !selChecked.has(getItemId(arr[i + 1]))
            ) {
                [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
            }
        }
        onSelectionChange(arr);
    }, [disabled, canMoveDown, selectedItems, selChecked, getItemId, onSelectionChange]);

    // ── Drag and Drop (HTML5) ────────────────────────────────────────────────

    const onDragStart = useCallback((index) => {
        setDragSrc(index);
    }, []);

    const onDragOver = useCallback((e, index) => {
        e.preventDefault(); // required to allow drop
        setDragOver(index);
    }, []);

    const onDrop = useCallback((e, targetIndex) => {
        e.preventDefault();
        if (dragSrc === null || dragSrc === targetIndex) {
            setDragSrc(null);
            setDragOver(null);
            return;
        }
        const arr = [...selectedItems];
        const [moved] = arr.splice(dragSrc, 1);
        arr.splice(targetIndex, 0, moved);
        onSelectionChange(arr);
        setDragSrc(null);
        setDragOver(null);
    }, [dragSrc, selectedItems, onSelectionChange]);

    const onDragEnd = useCallback(() => {
        setDragSrc(null);
        setDragOver(null);
    }, []);

    // ── Panel height ─────────────────────────────────────────────────────────

    const panelHeight =
        typeof height === "number" ? `${height}px` : (height || "400px");

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <div className={s.root} style={{ height: panelHeight }}>

            {/* ══════════════════════════════════════════════════════════════ */}
            {/* Panel 1 — Available Items                                     */}
            {/* ══════════════════════════════════════════════════════════════ */}
            <div className={s.panel}>
                <div className={s.panelHeader}>
                    <span className={s.panelTitle}>Available Items</span>
                    <Badge
                        appearance="filled"
                        color="brand"
                        shape="rounded"
                        size="small"
                    >
                        {filteredAvailable.length}
                    </Badge>
                </div>

                {searchable && (
                    <div className={s.searchBar}>
                        <Input
                            size="small"
                            contentBefore={<Search16Regular />}
                            placeholder="Search..."
                            value={search}
                            onChange={(_, data) => setSearch(data.value)}
                            disabled={disabled || loading}
                            style={{ width: "100%" }}
                            aria-label="Search available items"
                        />
                    </div>
                )}

                <div
                    className={s.listBody}
                    role="listbox"
                    aria-multiselectable="true"
                    aria-label="Available items"
                >
                    {loading ? (
                        <div className={s.loadingState}>
                            <Spinner size="medium" label="Loading items..." />
                        </div>
                    ) : filteredAvailable.length === 0 ? (
                        <div className={s.emptyState}>
                            <Text
                                size={200}
                                style={{ color: tokens.colorNeutralForeground3 }}
                            >
                                {search ? "No results found" : "No items available"}
                            </Text>
                            {search && (
                                <Text
                                    size={100}
                                    style={{ color: tokens.colorNeutralForeground3 }}
                                >
                                    Try clearing the search filter
                                </Text>
                            )}
                        </div>
                    ) : (
                        filteredAvailable.map((item) => {
                            const id = getItemId(item);
                            const label = getItemLabel(item);
                            const isChecked = availChecked.has(id);
                            return (
                                <div
                                    key={id}
                                    role="option"
                                    aria-selected={isChecked}
                                    tabIndex={disabled ? -1 : 0}
                                    className={mergeClasses(
                                        s.listItem,
                                        isChecked && s.listItemChecked,
                                    )}
                                    onClick={() => toggleAvailChecked(id)}
                                    onKeyDown={(e) => {
                                        if (e.key === " " || e.key === "Enter") {
                                            e.preventDefault();
                                            toggleAvailChecked(id);
                                        }
                                    }}
                                >
                                    {/*
                                     * Custom checkbox visual — aria-hidden because
                                     * the row itself carries role="option" + aria-selected.
                                     * pointer-events: none prevents double-firing.
                                     */}
                                    <span
                                        aria-hidden="true"
                                        className={mergeClasses(
                                            s.checkboxBox,
                                            isChecked && s.checkboxBoxChecked,
                                        )}
                                    >
                                        {isChecked && <CheckmarkSvg />}
                                    </span>
                                    <span className={s.itemLabel}>{label}</span>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* ── Add / Remove button column ───────────────────────────────── */}
            <div className={s.btnCol} aria-label="Transfer controls">
                <Tooltip content="Add selected" relationship="label">
                    <Button
                        size="small"
                        appearance="primary"
                        icon={<ArrowRight16Regular />}
                        onClick={handleAdd}
                        disabled={disabled || loading || !availChecked.size}
                        aria-label={`Add ${availChecked.size} item${availChecked.size !== 1 ? "s" : ""} to selected list`}
                    />
                </Tooltip>
                <Tooltip content="Remove selected" relationship="label">
                    <Button
                        size="small"
                        appearance="outline"
                        icon={<ArrowLeft16Regular />}
                        onClick={handleRemove}
                        disabled={disabled || !selChecked.size}
                        aria-label={`Remove ${selChecked.size} item${selChecked.size !== 1 ? "s" : ""} from selected list`}
                    />
                </Tooltip>
            </div>

            {/* ══════════════════════════════════════════════════════════════ */}
            {/* Panel 2 — Selected Items                                      */}
            {/* ══════════════════════════════════════════════════════════════ */}
            <div className={s.panel}>
                <div className={s.panelHeader}>
                    <span className={s.panelTitle}>Selected Items</span>
                    <Badge
                        appearance="filled"
                        color="success"
                        shape="rounded"
                        size="small"
                    >
                        {selectedItems.length}
                    </Badge>
                </div>

                <div
                    className={s.listBody}
                    role="listbox"
                    aria-multiselectable="true"
                    aria-label="Selected items — drag to reorder"
                >
                    {selectedItems.length === 0 ? (
                        <div className={s.emptyState}>
                            <Text
                                size={200}
                                style={{ color: tokens.colorNeutralForeground3 }}
                            >
                                No items selected
                            </Text>
                            <Text
                                size={100}
                                style={{ color: tokens.colorNeutralForeground3 }}
                            >
                                Add items from the available list
                            </Text>
                        </div>
                    ) : (
                        selectedItems.map((item, index) => {
                            const id = getItemId(item);
                            const label = getItemLabel(item);
                            const isChecked = selChecked.has(id);
                            const isDragging = dragSrc === index;
                            const isDropTarget =
                                dragOver === index && dragSrc !== index;

                            return (
                                <div
                                    key={id}
                                    role="option"
                                    aria-selected={isChecked}
                                    aria-label={`${label}, position ${index + 1}`}
                                    tabIndex={disabled ? -1 : 0}
                                    draggable={!disabled}
                                    className={mergeClasses(
                                        s.listItem,
                                        isChecked && s.listItemChecked,
                                        isDragging && s.listItemDragging,
                                        isDropTarget && s.listItemDragTarget,
                                    )}
                                    onClick={() => toggleSelChecked(id)}
                                    onKeyDown={(e) => {
                                        if (e.key === " " || e.key === "Enter") {
                                            e.preventDefault();
                                            toggleSelChecked(id);
                                        }
                                    }}
                                    onDragStart={() => onDragStart(index)}
                                    onDragOver={(e) => onDragOver(e, index)}
                                    onDrop={(e) => onDrop(e, index)}
                                    onDragEnd={onDragEnd}
                                >
                                    {/* Drag handle — stopPropagation so it doesn't toggle the row */}
                                    <span
                                        className={s.dragHandle}
                                        aria-hidden="true"
                                        title="Drag to reorder"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <ReOrderDotsVertical16Regular />
                                    </span>

                                    <span
                                        aria-hidden="true"
                                        className={mergeClasses(
                                            s.checkboxBox,
                                            isChecked && s.checkboxBoxChecked,
                                        )}
                                    >
                                        {isChecked && <CheckmarkSvg />}
                                    </span>

                                    <span className={s.itemLabel}>{label}</span>

                                    <span aria-hidden="true" className={s.orderNum}>
                                        #{index + 1}
                                    </span>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* ── Move Up / Move Down button column ────────────────────────── */}
            <div className={s.btnCol} aria-label="Reorder controls">
                <Tooltip content="Move up" relationship="label">
                    <Button
                        size="small"
                        appearance="outline"
                        icon={<ArrowUp16Regular />}
                        onClick={handleMoveUp}
                        disabled={disabled || !canMoveUp}
                        aria-label="Move selected items up"
                    />
                </Tooltip>
                <Tooltip content="Move down" relationship="label">
                    <Button
                        size="small"
                        appearance="outline"
                        icon={<ArrowDown16Regular />}
                        onClick={handleMoveDown}
                        disabled={disabled || !canMoveDown}
                        aria-label="Move selected items down"
                    />
                </Tooltip>
            </div>

            {/* ══════════════════════════════════════════════════════════════ */}
            {/* Panel 3 — Final Order Preview (read-only, optional)           */}
            {/* ══════════════════════════════════════════════════════════════ */}
            {showFinalOrder && (
                <div className={s.panel}>
                    <div className={s.panelHeader}>
                        <span className={s.panelTitle}>Final Order</span>
                        <Badge appearance="outline" shape="rounded" size="small">
                            {selectedItems.length}
                        </Badge>
                    </div>

                    <div
                        className={s.listBody}
                        role="list"
                        aria-label="Final order preview (read-only)"
                        aria-live="polite"
                        aria-atomic="false"
                    >
                        {selectedItems.length === 0 ? (
                            <div className={s.emptyState}>
                                <Text
                                    size={200}
                                    style={{ color: tokens.colorNeutralForeground3 }}
                                >
                                    No items selected
                                </Text>
                            </div>
                        ) : (
                            selectedItems.map((item, index) => {
                                const id = getItemId(item);
                                const label = getItemLabel(item);
                                return (
                                    <div
                                        key={id}
                                        role="listitem"
                                        className={s.previewItem}
                                        aria-label={`Position ${index + 1}: ${label}`}
                                    >
                                        <span
                                            className={s.seqCircle}
                                            aria-hidden="true"
                                        >
                                            {index + 1}
                                        </span>
                                        <span className={s.itemLabel}>{label}</span>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}

        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// PropTypes & DefaultProps
// ─────────────────────────────────────────────────────────────────────────────

OrderedMultiSelectPickList.propTypes = {
    /** Full source list of items the user can choose from */
    availableItems: PropTypes.array.isRequired,
    /** Currently selected items in their display order */
    selectedItems: PropTypes.array,
    /** Returns a unique string/number ID for a given item */
    getItemId: PropTypes.func.isRequired,
    /** Returns the display label string for a given item */
    getItemLabel: PropTypes.func.isRequired,
    /** Called with the updated ordered array whenever selection changes */
    onSelectionChange: PropTypes.func.isRequired,
    /** Show the search input above the available list */
    searchable: PropTypes.bool,
    /** Disable all interactions */
    disabled: PropTypes.bool,
    /** Show a loading spinner in place of the available list */
    loading: PropTypes.bool,
    /** Show or hide the Final Order preview panel */
    showFinalOrder: PropTypes.bool,
    /** Overall panel height — number (px) or any valid CSS length string. Defaults to 400. */
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
